import React from 'react';
import { useDispatch } from 'react-redux';
import { IconButton, Tooltip } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { Icon, useSettings } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';
import { PaginationType } from 'app/main/components/hooks/usePagination';

import buttons from './PhoneCallsButtons';
import { showPhoneCallsEditNotesDialog } from './components/PhoneCallsEditNotesDialog';

interface PhoneCallsTableProps {
	paginationProps?: PaginationType;
	showLinkedItem?: boolean;
	showActions?: boolean;
	refresh: boolean;
	updateRefresh: () => void;
}

export default function PhoneCallsTable({
	paginationProps,
	showLinkedItem,
	showActions,
	refresh,
	updateRefresh
}: PhoneCallsTableProps) {
	const { settings, t } = useSettings('phonecalls');
	const dispatch = useDispatch();

	function editRowNotes(row) {
		dispatch(showPhoneCallsEditNotesDialog({ model: row, updateRefresh }));
	}

	const columns: GridColDef[] = [
		{
			field: 'phca_d_from',
			headerName: t('PHONECALLS_COLUMN_DATE'),
			minWidth: 200,
			renderCell: ({ value }) =>
				ViewTableUtils.renderDate({
					value,
					format: {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit'
					}
				}),
			sortable: false,
			hideable: false
		},
		{
			field: 'phca_direction',
			headerName: t('PHONECALLS_COLUMN_DIRECTION'),
			minWidth: 150,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value: t(`PHONECALLS_DIRECTION_${value}`),
					icon: <Icon icon={`phone-${value}`} />,
					backgroundColor: theme =>
						row.phca_is_answered === 1 ? theme.palette.success.main : theme.palette.error.main
				}),
			sortable: false,
			hideable: false
		},
		{
			field: 'phca_duration',
			headerName: t('PHONECALLS_COLUMN_TALKING_DURATION'),
			type: 'number',
			minWidth: 150,
			renderCell: ({ value }) => ViewTableUtils.renderDuration({ seconds: value }),
			sortable: false
		}
	];

	if (showLinkedItem)
		columns.push({
			field: 'phon_table_uid',
			headerName: t('PHONECALLS_COLUMN_PHONE_ITEM'),
			minWidth: 250,
			flex: 2,
			renderCell: ({ row }) =>
				ViewTableUtils.renderCellWithTooltip({
					value: row.peop_title || row.comp_title || row.user_title,
					to: {
						module: row.peop_uid ? 'people' : row.comp_uid ? 'companies' : 'users',
						uid: row.peop_uid || row.comp_uid || row.user_uid
					}
				}),
			sortable: false
		});

	columns.push(
		{
			field: 'phca_number',
			headerName: t('PHONECALLS_COLUMN_PHONE_NUMBER'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value: value || row.phca_src_number || row.phca_dst_number }),
			sortable: false,
			hideable: false
		},
		{
			field: 'phon_description',
			headerName: t('PHONECALLS_COLUMN_PHONE_DESCRIPTION'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value: value || <i>{row.phca_contact_title}</i> }),
			sortable: false
		},
		{
			field: 'phca_import_type',
			headerName: t('PHONECALLS_COLUMN_PHONE_IMPORT_TYPE'),
			minWidth: 150,
			flex: 1,
			renderCell: ({ value }) =>
				ViewTableUtils.renderSquareChip({
					value,
					icon: <Icon icon="import" />
				}),
			sortable: false
		},
		{
			field: 'call_user_uid',
			headerName: t('PHONECALLS_COLUMN_PHONE_CALL_USER'),
			minWidth: 250,
			flex: 2,
			renderCell: ({ row }) =>
				ViewTableUtils.renderCellWithTooltip({
					value: row.call_user_title,
					to: { module: 'users', uid: row.call_user_uid }
				}),
			sortable: false
		}
	);

	if (showActions)
		columns.push({
			type: 'actions',
			field: 'actions',
			width: 80,
			renderCell: ({ row }) => (
				<>
					<Tooltip title={row.phca_note || ''} placement="left" arrow>
						<IconButton onClick={() => editRowNotes(row)}>
							<Icon icon={row.phca_note ? 'comment-text-outline' : 'comment-outline'} />
						</IconButton>
					</Tooltip>
					{ViewTableUtils.renderMenu({
						items: buttons,
						buttonProps: {
							settings,
							model: row,
							updateRefresh
						}
					})}
				</>
			),
			sortable: false
		});

	return (
		<ViewTable
			module="phonecalls"
			columns={columns}
			paginationProps={paginationProps}
			refresh={refresh}
			updateRefresh={updateRefresh}
		/>
	);
}
